import React, { useState, useEffect, useContext } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { gql, useApolloClient } from '@apollo/client'
import SimpleMdeReact from 'react-simplemde-editor'
import 'easymde/dist/easymde.min.css'

// contexts
import { UserContext } from '../contexts/UserContext'

// assets
import spinner from '../spinner.svg'

const CATEGORIES = gql`
  query Categories {
    categories {
      data {
        id
        attributes {
          name
        }
      }
    }
  }
`

const ADD_REVIEW = gql`
  mutation AddReview(
    $title: String!
    $rating: Int!
    $body: String!
    $categories: [ID]!
    $author: ID!
  ) {
    createReview(
      data: {
        title: $title
        rating: $rating
        body: $body
        categories: $categories
        author: $author
      }
    ) {
      data {
        id
        attributes {
          title
          categories {
            data {
              id
              attributes {
                name
              }
            }
          }
          author {
            data {
              id
              attributes {
                username
              }
            }
          }
        }
      }
    }
  }
`

const AddReview = () => {
  const [categories, setCategories] = useState(null)
  const [loadingCategory, setLoadingCategory] = useState(true)

  const { user, loading } = useContext(UserContext)

  const client = useApolloClient()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    title: '',
    rating: '',
    body: '',
    categories: [],
    author: ''
  })
  const [reviewError, setReviewError] = useState('')
  const [errors, setErrors] = useState({
    title: false,
    rating: false,
    body: false
  })

  const handleCategory = (e) => {
    if (e.target.checked) {
      setFormData({
        ...formData,
        categories: [...formData.categories, e.target.value]
      })
    } else {
      setFormData({
        ...formData,
        categories: formData.categories.filter(
          (category) => category !== e.target.value
        )
      })
    }
  }

  const onRatingChange = (e) => {
    const rating = e.target.value
    if (!rating || rating.match(/^([1-9]|10)$/)) {
      setFormData({ ...formData, rating })
    }
  }

  const onChangeBody = (value) => {
    setFormData({ ...formData, body: value })
  }

  const handleAddReview = async (e) => {
    e.preventDefault()

    formData.title.trim()
      ? setErrors((prevState) => ({ ...prevState, title: false }))
      : setErrors((prevState) => ({ ...prevState, title: true }))

    formData.rating
      ? setErrors((prevState) => ({ ...prevState, rating: false }))
      : setErrors((prevState) => ({ ...prevState, rating: true }))

    formData.body.trim()
      ? setErrors((prevState) => ({ ...prevState, body: false }))
      : setErrors((prevState) => ({ ...prevState, body: true }))

    if (formData.title.trim() && formData.rating && formData.body.trim()) {
      try {
        const token = localStorage.getItem('myReviewsToken')
        const data = await client.mutate({
          mutation: ADD_REVIEW,
          variables: {
            title: formData.title,
            rating: Number(formData.rating),
            body: formData.body,
            categories: formData.categories,
            author: user.id
          },
          context: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        })

        navigate('/')
      } catch (error) {
        setReviewError(error.message)
      }
    }
  }

  useEffect(() => {
    setLoadingCategory(true)

    const fetchCategoriesData = async () => {
      try {
        const data = await client.query({
          query: CATEGORIES
        })

        setCategories(data)
        setLoadingCategory(false)
      } catch (error) {
        setLoadingCategory(false)
      }
    }

    fetchCategoriesData()
  }, [client])

  if (loading || loadingCategory)
    return <img src={spinner} className="spinner" alt="loading" />

  if (!user) return <Navigate to="/login" />

  return (
    <>
      {reviewError && (
        <div className="custom-card-top alert-danger">{reviewError}</div>
      )}
      <div className="custom-card">
        <div className="custom-card-title">Add Review</div>
        <form noValidate>
          <div className="mb-5">
            <label htmlFor="title">
              Title <small>(required)</small>
            </label>
            <input
              type="text"
              name="title"
              id="title"
              className={`${errors.title && 'invalid'}`}
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value })
              }}
            />
            {errors.title && (
              <small className="small fw-light invalid-feedback">
                The title field is required
              </small>
            )}
          </div>
          <div className="mb-5">
            <label htmlFor="rating">
              Rating <small>(required)</small>
            </label>
            <input
              type="text"
              name="rating"
              id="rating"
              className={`${errors.rating && 'invalid'}`}
              value={formData.rating}
              onChange={onRatingChange}
            />
            <small className="fw-light text-muted">
              The rating needs to be between 1 and 10
            </small>
            {errors.rating && (
              <div className="small fw-light invalid-feedback">
                The rating field is required
              </div>
            )}
          </div>
          <div className="mb-5">
            <label htmlFor="body">
              Body <small>(required)</small>
            </label>
            <SimpleMdeReact value={formData.body} onChange={onChangeBody} />
            {errors.body && (
              <small className="small fw-light invalid-feedback">
                The body field is required
              </small>
            )}
          </div>
          <div className="mb-0">
            <label htmlFor="category">
              Categories <small>(required)</small>
            </label>
            <div className="mb-5">
              {categories.data.categories.data.map((catagory) => (
                <label
                  className="customcheck customcheck-sm mr-5"
                  key={catagory.id}
                >
                  {catagory.attributes.name}
                  <input
                    type="checkbox"
                    name={`category_${catagory.id}`}
                    id={`category_${catagory.id}`}
                    value={catagory.id}
                    onChange={handleCategory}
                  />
                  <span className="checkmark"></span>
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="btn" onClick={handleAddReview}>
            Add Review
          </button>
        </form>
      </div>
    </>
  )
}

export default AddReview
